/**
 * The anti-black-screen harness.
 *
 * FOERDERWERK's lesson, ported: an API-green black screen passes every test you would
 * naturally write. So the gate reads the actual framebuffer and asserts on its statistics:
 * mean luminance, standard deviation, coverage, distinct colours, and the fraction of
 * pixels carrying each of the palette's identity hues.
 */

import type { Stage } from '../scene/stage';

export interface PixelStats {
  mean: number;
  sd: number;
  /** % of pixels brighter than near-black */
  coverage: number;
  /** distinct colours at 4-bit-per-channel quantisation */
  colors: number;
  /** % of pixels that are warm (kitsch chassis) vs cool (neon/night) */
  warm: number;
  cool: number;
  /** % of pixels that are strongly saturated — the neon signature */
  neon: number;
  /** % of near-black pixels — the ink outlines and the night */
  ink: number;
  w: number;
  h: number;
}

export function readPixelStats(stage: Stage): PixelStats {
  const gl = stage.renderer.getContext();
  const w = gl.drawingBufferWidth, h = gl.drawingBufferHeight;
  const buf = new Uint8Array(w * h * 4);
  gl.readPixels(0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, buf);

  let sum = 0, sum2 = 0, n = 0, cov = 0, warm = 0, cool = 0, neon = 0, ink = 0;
  const seen = new Set<number>();
  for (let i = 0; i < buf.length; i += 4) {
    const r = buf[i], g = buf[i + 1], b = buf[i + 2];
    const l = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    sum += l; sum2 += l * l; n++;
    if (l > 8) cov++;
    if (l < 26) ink++;
    const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
    const sat = mx === 0 ? 0 : (mx - mn) / mx;
    if (sat > 0.45 && mx > 90) neon++;
    if (r > b + 10) warm++; else if (b > r + 10) cool++;
    seen.add(((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4));
  }
  const mean = sum / n;
  return {
    mean,
    sd: Math.sqrt(Math.max(0, sum2 / n - mean * mean)),
    coverage: (100 * cov) / n,
    colors: seen.size,
    warm: (100 * warm) / n,
    cool: (100 * cool) / n,
    neon: (100 * neon) / n,
    ink: (100 * ink) / n,
    w, h,
  };
}
